// دسته‌های کالا (SCR-STAFF-870؛ C-AGENT-764): فهرست تخت با کد یکتا، افزودن و حذف دسته خالی.
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, LoadingState } from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { inventoryKeys, useItemCategories } from './inventoryCommon';

export function CategoriesDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('inv.item.manage');
  const categories = useItemCategories();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: inventoryKeys.categories(session?.tenantId) });

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/item-categories', {
          body: { code: code.trim(), name: name.trim(), sortOrder: categories.data?.items.length ?? 0 },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setCode('');
      setName('');
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.DELETE('/api/v1/inventory/item-categories/{id}', { params: { path: { id } } })),
    onSuccess: refresh,
  });

  const error = create.error ?? remove.error;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="inv-categories-title">
      <DialogTitle id="inv-categories-title">{t('inv:categories.title')}</DialogTitle>
      <DialogContent>
        {categories.isPending && <LoadingState rows={3} />}
        {categories.error && <ErrorState error={categories.error} onRetry={() => void categories.refetch()} />}
        {categories.data && (
          <List dense data-testid="item-category-list">
            {categories.data.items.length === 0 && <ListItem>{t('inv:categories.empty')}</ListItem>}
            {categories.data.items.map((category) => (
              <ListItem
                key={category.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`${t('inv:categories.delete')} ${category.name}`}
                    disabled={!write.canWrite || Number(category.itemCount) > 0}
                    onClick={() => remove.mutate(category.id)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={category.name} secondary={`${category.code} · ${t('inv:categories.items', { count: Number(category.itemCount), value: faDigits(Number(category.itemCount)) })}`} />
              </ListItem>
            ))}
          </List>
        )}
        {error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {t('inv:categories.error')}
          </Alert>
        ) : null}
        {write.canWrite && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
            <TextField size="small" label={t('inv:categories.code')} value={code} onChange={(event) => setCode(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'category-code' } }} />
            <TextField size="small" label={t('inv:categories.name')} value={name} onChange={(event) => setName(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'category-name' } }} />
            <Button variant="contained" onClick={() => create.mutate()} disabled={!code.trim() || !name.trim() || create.isPending} data-testid="category-add">
              {t('inv:categories.add')}
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
